import { type FC, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { usePlatform, useTranslation } from "@/shared/hooks";
import { getStripePromise } from "@/shared/lib/stripe/getStripePromise";
import { useStripeElementsOptions } from "@/shared/lib/stripe/useStripeElementsOptions";
import { PaymentTerms } from "../PaymentTerms";
import { getPaymentTermsUrls } from "../paymentTermsUrls";

interface CheckoutDisclosure {
  /** Название продукта: покупатель должен видеть, за что платит. */
  productTitle: string;
  /** Сумма списания, уже отформатированная (formatCurrency). */
  amountLabel: string;
  /** Первое списание, если оно отличается от цены продления (платный триал,
   * промокод); иначе null. */
  firstChargeLabel: string | null;
  /** Для подписки: «$9 / месяц», иначе null. */
  priceOnPeriod: string | null;
  recurrent: boolean;
}

interface InnerFormProps extends CheckoutDisclosure {
  clientSecret: string;
  onConfirmed: () => void;
}

// Stripe отвечает этим текстом, когда банк требует платёжный адрес: легаси
// ловит его так же по подстроке (StripeCheckout.js:110-117), отдельного кода
// ошибки у Stripe для этого случая нет.
const BILLING_DETAILS_ERROR = "We are unable to authenticate";

const InnerForm: FC<InnerFormProps> = ({
  clientSecret,
  onConfirmed,
  productTitle,
  amountLabel,
  firstChargeLabel,
  priceOnPeriod,
  recurrent,
}) => {
  const { t } = useTranslation();
  const { platform } = usePlatform();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Согласие с условиями и политикой возврата прямо перед оплатой (если у
  // платформы заданы оба url); без галочки кнопка «Оплатить» недоступна.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const termsRequired = !!getPaymentTermsUrls(platform);
  // Пока iframe Stripe грузится, в диалоге пусто: показываем спиннер и
  // прячем форму/кнопку до onReady, чтобы не было прыжка и пустого окна
  // (референс StripePaymentForm.js:33,92 + StripeCheckout.js:148-152,207 —
  // там индикатор висит до onReady, а кнопка оплаты появляется только
  // после; у нас вместо LinearProgress тот же CircularProgress, что и на
  // остальных шагах диалога).
  const [ready, setReady] = useState(false);
  // Банк отказал без платёжного адреса: показываем AddressElement и даём
  // оплатить ещё раз (референс StripeCheckout.js:110-117,160-165,191-195).
  const [needBillingDetails, setNeedBillingDetails] = useState(false);
  // PaymentElement может не загрузиться вовсе (сеть, блокировщик, чужой
  // clientSecret): без этой ветки спиннер крутился бы вечно над пустотой.
  const [loadFailed, setLoadFailed] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    // Trial subscriptions go through a SetupIntent (no charge yet — see
    // Task 4/8), identifiable by its "seti_" clientSecret prefix, while
    // regular payments confirm a PaymentIntent ("pi_"). The two confirm*
    // methods' overloads don't unify into a single callable when merged via
    // a ternary, so branch explicitly instead (Task 9).
    const { error: confirmError } = clientSecret.startsWith("seti_")
      ? await stripe.confirmSetup({ elements, redirect: "if_required" })
      : await stripe.confirmPayment({ elements, redirect: "if_required" });

    setSubmitting(false);

    if (confirmError) {
      if (
        !needBillingDetails &&
        confirmError.message?.includes(BILLING_DETAILS_ERROR)
      ) {
        setNeedBillingDetails(true);
        return;
      }
      setError(confirmError.message ?? t("payment.errorTitle"));
      return;
    }

    onConfirmed();
  };

  // Триальная подписка подтверждается SetupIntent'ом: карта привязывается
  // без списания, поэтому и подпись кнопки другая (референс
  // StripeCheckout.js:226-229).
  const isSetupIntent = clientSecret.startsWith("seti_");

  return (
    <Box>
      {loadFailed && <Alert severity="error">{t("payment.errorTitle")}</Alert>}
      {needBillingDetails && (
        <Alert icon={false} severity="info" sx={{ mb: 2 }}>
          {t("payment.billingDetailsRequired")}
        </Alert>
      )}
      {!ready && !loadFailed && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      <Box sx={{ display: ready ? "block" : "none" }}>
        <PaymentElement
          // Референс StripeCheckout.js:171-187: платёжные поля банка по
          // усмотрению Stripe, а собственные условия про сохранение карты
          // он не рисует (их показывает блок ниже).
          options={{
            fields: { billingDetails: "auto" },
            terms: { card: "never" },
          }}
          onReady={() => setReady(true)}
          // Сообщение Stripe тут техническое («Invalid client secret»),
          // покупателю показываем общий текст ошибки платежа.
          onLoadError={() => setLoadFailed(true)}
        />
      </Box>
      {ready && needBillingDetails && (
        <Box sx={{ mt: 2 }}>
          <AddressElement options={{ mode: "billing" }} />
        </Box>
      )}
      {/* Условия автосписания прямо перед кнопкой оплаты (референс
        PaymentForm.js:57-131 + StripeCheckout.js:196-201): что за продукт,
        сколько и как часто списывается, и что отменить можно в любой
        момент. Комиссии сервиса в схеме витрины нет, эта строка легаси
        здесь не воспроизводится. */}
      {recurrent && !loadFailed && (
        <Alert icon={false} severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            {productTitle}
          </Typography>
          {firstChargeLabel && (
            <Typography variant="body2" gutterBottom>
              {t("payment.firstChargeAmount", { amount: firstChargeLabel })}
            </Typography>
          )}
          {priceOnPeriod && (
            <Typography variant="body2" gutterBottom>
              {t("payment.subscriptionDescription", { priceOnPeriod })}
            </Typography>
          )}
          <Typography variant="body2">
            {t("payment.canCancelAnyTime")}
          </Typography>
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {ready && termsRequired && (
        <Box sx={{ mt: 2 }}>
          <PaymentTerms
            always
            currency=""
            checked={termsAccepted}
            onChange={setTermsAccepted}
          />
        </Box>
      )}
      <Button
        fullWidth
        variant="contained"
        sx={{ mt: 2, display: ready ? undefined : "none" }}
        disabled={!stripe || submitting || (termsRequired && !termsAccepted)}
        onClick={handleSubmit}
        // Спиннер занимает место иконки, а не текста: покупатель всё время
        // видит, за что платит (референс StripeCheckout.js:216-224).
        startIcon={
          submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <SendIcon />
          )
        }
      >
        {isSetupIntent
          ? t("payment.connectCardButton")
          : t("payment.payButton", { amount: amountLabel })}
      </Button>
    </Box>
  );
};

interface StripeCheckoutStepProps extends CheckoutDisclosure {
  publishableKey: string;
  clientSecret: string;
  onConfirmed: () => void;
}

export const StripeCheckoutStep: FC<StripeCheckoutStepProps> = ({
  publishableKey,
  clientSecret,
  onConfirmed,
  productTitle,
  amountLabel,
  firstChargeLabel,
  priceOnPeriod,
  recurrent,
}) => {
  const stripePromise = getStripePromise(publishableKey);
  const options = useStripeElementsOptions(clientSecret);

  return (
    <Elements stripe={stripePromise} options={options}>
      <InnerForm
        clientSecret={clientSecret}
        onConfirmed={onConfirmed}
        productTitle={productTitle}
        amountLabel={amountLabel}
        firstChargeLabel={firstChargeLabel}
        priceOnPeriod={priceOnPeriod}
        recurrent={recurrent}
      />
    </Elements>
  );
};
